import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { X, Play, FileText, Download } from "lucide-react";
import api from "../helpers/axios";
import ModalPortal from "./ModalPortal";

const formatBytes = (bytes) => {
  if (typeof bytes !== "number") return "";
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Attachment files live behind the authenticated backend proxy, so a plain
// <img src> / <video src> can't load them (the browser won't send the
// Authorization header). Every file is fetched as a blob through the axios
// instance and rendered from an object URL instead.
const fetchBlobUrl = async (path) => {
  const res = await api.get(path, { responseType: "blob" });
  return URL.createObjectURL(res.data);
};

const chipClass =
  "flex items-center gap-2.5 rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-left hover:bg-gray-100 transition";

const Spinner = () => (
  <div className="h-4 w-4 flex-shrink-0 animate-spin rounded-full border-2 border-[#2B3674]/30 border-t-[#2B3674]" />
);

// One attachment on a support ticket comment. Images show a thumbnail that
// opens full-size in a lightbox; videos load into a player on click (they can
// be tens of MB, so never auto-loaded); anything else is a download chip.
const CommentAttachment = ({ attachment }) => {
  const isImage = attachment.mime_type?.startsWith("image/");
  const isVideo = attachment.mime_type?.startsWith("video/");

  const [thumbUrl, setThumbUrl] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [mediaUrl, setMediaUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  // Image thumbnails load right away; videos and other files wait for a click.
  useEffect(() => {
    if (!isImage) return;

    let cancelled = false;
    let objectUrl = null;
    fetchBlobUrl(attachment.thumbnail_url ?? attachment.content_url)
      .then((url) => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        objectUrl = url;
        setThumbUrl(url);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment, isImage]);

  // Revoke the click-loaded object URL when it's replaced or on unmount.
  useEffect(() => {
    return () => {
      if (mediaUrl) URL.revokeObjectURL(mediaUrl);
    };
  }, [mediaUrl]);

  const loadFullFile = async (onLoaded) => {
    if (loading) return;
    setLoading(true);
    try {
      const url = await fetchBlobUrl(attachment.content_url);
      onLoaded(url);
    } catch {
      toast.error("Failed to load attachment");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () =>
    loadFullFile((url) => {
      const link = document.createElement("a");
      link.href = url;
      link.download = attachment.filename;
      link.click();
      URL.revokeObjectURL(url);
    });

  const sizeLabel = formatBytes(attachment.size_bytes);

  // Open the lightbox right away and load the file inside it — the
  // full-screen overlay blocks clicks on other attachments, so only one
  // preview can be open at a time. The loaded media is kept for reopening.
  const openPreview = () => {
    setPreviewOpen(true);
    if (mediaUrl || loading) return;
    setLoading(true);
    fetchBlobUrl(attachment.content_url)
      .then(setMediaUrl)
      .catch(() => {
        toast.error("Failed to load attachment");
        setPreviewOpen(false);
      })
      .finally(() => setLoading(false));
  };

  // Shared full-screen lightbox for images and videos.
  const lightbox = previewOpen && (
    <ModalPortal>
      <div
        className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-75 p-8"
        onClick={() => setPreviewOpen(false)}
      >
        <button
          onClick={() => setPreviewOpen(false)}
          title="Close"
          className="absolute top-4 right-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/25 transition"
        >
          <X size={20} />
        </button>
        {!mediaUrl ? (
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-white/30 border-t-white" />
        ) : isVideo ? (
          <video
            controls
            autoPlay
            src={mediaUrl}
            onClick={(e) => e.stopPropagation()}
            className="max-h-full max-w-full rounded-lg"
          />
        ) : (
          <img
            src={mediaUrl}
            alt={attachment.filename}
            onClick={(e) => e.stopPropagation()}
            className="max-h-full max-w-full rounded-lg object-contain"
          />
        )}
      </div>
    </ModalPortal>
  );

  if (isImage) {
    return (
      <>
        <button
          onClick={openPreview}
          title={attachment.filename}
          className="group relative h-20 w-20 overflow-hidden rounded-lg border border-gray-200 bg-gray-100"
        >
          {thumbUrl ? (
            <img
              src={thumbUrl}
              alt={attachment.filename}
              className="h-full w-full object-cover transition group-hover:scale-105"
            />
          ) : (
            <div className="h-full w-full animate-pulse bg-gray-200" />
          )}
        </button>
        {lightbox}
      </>
    );
  }

  if (isVideo) {
    return (
      <>
        <button onClick={openPreview} className={chipClass}>
          <Play size={15} className="flex-shrink-0 text-[#2B3674]" />
          <span className="max-w-[180px] truncate text-sm text-gray-700">
            {attachment.filename}
          </span>
          <span className="text-xs text-gray-400">{sizeLabel}</span>
        </button>
        {lightbox}
      </>
    );
  }

  return (
    <button onClick={handleDownload} className={chipClass}>
      {loading ? <Spinner /> : <FileText size={15} className="flex-shrink-0 text-[#2B3674]" />}
      <span className="max-w-[180px] truncate text-sm text-gray-700">
        {attachment.filename}
      </span>
      <span className="text-xs text-gray-400">{sizeLabel}</span>
      <Download size={13} className="flex-shrink-0 text-gray-400" />
    </button>
  );
};

export default CommentAttachment;
